// Shared UI primitive (reused across all dashboard pages) — MIS-809.
//
// A dependency-free button: plain Tailwind + the `cn()` helper, no Radix/cva.
// `variant`/`size` pick a class string from a small lookup; `forwardRef` so it
// can be used as a form submit target or focused programmatically. Defaults to
// `type="button"` so a button inside a <form> never submits by accident.

import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export type ButtonVariant = 'default' | 'destructive' | 'outline' | 'ghost';
export type ButtonSize = 'default' | 'sm' | 'lg' | 'icon';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

// `default` keeps `bg-gray-900 text-white` so the index.css `.bg-gray-900.text-white`
// the AI-Studio teal→mint gradient. The rest target the dark tokens.
const VARIANTS: Record<ButtonVariant, string> = {
  default:
    'bg-[linear-gradient(135deg,#2dffc4,#00d4aa)] text-[#08080d] shadow-[0_6px_20px_rgba(0,212,170,0.25)] hover:brightness-110',
  destructive: 'bg-red-600 text-white hover:bg-red-700',
  outline:
    'border border-[var(--border-strong)] bg-transparent text-[var(--text)] hover:bg-[var(--surface-3)]',
  ghost: 'text-[var(--muted)] hover:bg-[var(--surface-3)] hover:text-[var(--text)]',
};

const SIZES: Record<ButtonSize, string> = {
  default: 'h-9 px-4 py-2 text-sm',
  sm: 'h-8 px-3 text-xs',
  lg: 'h-10 px-6 text-sm',
  icon: 'h-9 w-9',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
